import React, { useState } from "react";
import {
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  ImagePlus,
  Link2,
  NotebookText,
  Plus,
  ReceiptText,
  RotateCcw,
  Save,
  ShieldCheck,
  Trash2,
  UserRound,
} from "lucide-react";
import Button from "./ui/Button";
import {
  createGeneralQuoteSpecification,
  createGeneralQuoteItem,
  getGeneralItemAmount,
  getGeneralItemTaxAmount,
  normalizeGeneralQuoteItemSpecifications,
  normalizeGeneralQuoteItemImages,
  syncGeneralQuoteItemPrimaryImageFields,
} from "./generalQuotationUtils";
import useViewport from "./useViewport";

const cardStyle = (isDark) => ({
  background: isDark ? "#0f172a" : "#ffffff",
  borderRadius: 20,
  border: isDark
    ? "1px solid rgba(148,163,184,0.16)"
    : "1px solid rgba(226,232,240,0.9)",
  boxShadow: isDark
    ? "0 16px 40px rgba(2,6,23,0.35)"
    : "0 18px 36px rgba(15,23,42,0.06)",
  padding: 24,
});

const runProtectedQuoteAction = (event, handler) => {
  event?.preventDefault?.();
  event?.stopPropagation?.();
  return handler?.(event);
};

const fieldLabelStyle = (isDark) => ({
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: isDark ? "#94a3b8" : "#64748b",
  marginBottom: 8,
});

const fieldStyle = (isDark, multiline = false) => ({
  width: "100%",
  borderRadius: 14,
  border: isDark
    ? "1px solid rgba(148,163,184,0.2)"
    : "1px solid #dbe4f0",
  background: isDark ? "#020617" : "#f8fafc",
  color: isDark ? "#e2e8f0" : "#0f172a",
  padding: multiline ? "12px 14px" : "11px 14px",
  fontSize: 14,
  lineHeight: 1.5,
  outline: "none",
  resize: multiline ? "vertical" : "none",
  minHeight: multiline ? 110 : "auto",
  boxSizing: "border-box",
});

const iconBadgeStyle = (isDark) => ({
  width: 36,
  height: 36,
  minWidth: 36,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: isDark ? "rgba(37,99,235,0.16)" : "#eff6ff",
  color: "#2563eb",
});

const isTokenlessFirebaseStorageUrl = (url = "") =>
  String(url || "").includes("firebasestorage.googleapis.com") &&
  !String(url || "").includes("token=");

const isFirebaseStorageRestUrl = (url = "") =>
  String(url || "").includes("firebasestorage.googleapis.com/v0/b/");

const getImageDisplayUrl = (image = {}) => {
  const url = String(
    image?.thumbnailDownloadURL ||
      image?.previewUrl ||
      image?.downloadURL ||
      image?.url ||
      ""
  ).trim();
  return isTokenlessFirebaseStorageUrl(url) ? "" : url;
};

const getImageFullUrl = (image = {}) => {
  const url = String(image?.downloadURL || image?.url || image?.previewUrl || "").trim();
  return isTokenlessFirebaseStorageUrl(url) ? "" : url;
};

const getImageUnavailableMessage = (image = {}) =>
  String(image?.resolveErrorMessage || "").trim() ||
  (image?.fullPath || image?.storagePath || image?.path
    ? "Image unavailable"
    : "Image unavailable: file path missing");

const getImageUnavailableBody = (image = {}, repairState = null) => {
  const message = getImageUnavailableMessage(image);
  if (
    repairState?.status === "missing" ||
    /not found|object-not-found/i.test(
      `${message} ${image?.resolveErrorCode || ""} ${repairState?.error || ""}`
    )
  ) {
    return "The original image file was not found in Firebase Storage.";
  }
  return message;
};

const StatRow = ({ label, value, highlight, isDark }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: 14,
      fontWeight: highlight ? 800 : 600,
      color: highlight ? "#2563eb" : isDark ? "#e2e8f0" : "#0f172a",
    }}
  >
    <span style={{ opacity: highlight ? 1 : 0.8 }}>{label}</span>
    <span>{value}</span>
  </div>
);

export default function GeneralQuotationEditor({
  quote,
  setQuote,
  totals,
  isDark,
  businessName,
  logoURL,
  shopAddress,
  currencySymbol,
  isSaving,
  autoSaveStatus,
  localDraftStatus = { state: "idle", savedAt: null },
  editorMode = "new",
  editingQuoteLabel = "",
  onStartNewQuote,
  onSave,
  onSaveAsNew,
  onExportPdf,
  onExportExecutionPdf,
  canSave = true,
  canChangeImages = true,
  canSaveAsNew = true,
  canStartNewQuote = true,
  canExportPdf = true,
  canExportExecutionPdf = true,
  onUploadItemImage,
  onRemoveItemImage,
  onRetryItemImage,
  onRepairItemImage,
  onReplaceItemImage,
  onCopyImageDebug,
  imageRepairState = {},
  imageRepairCandidates = {},
  onSelectRepairCandidate,
  onRemoveItem,
  onDraftBlur,
  uploadingItemId,
  executionTemplates = [],
  customerSelector = null,
  siteSelector = null,
  referralPartnerSelector = null,
  validationError = null,
}) {
  const { isMobile, isCompact } = useViewport();
  const hasUploadInProgress = Boolean(uploadingItemId);
  const [expandedItems, setExpandedItems] = useState({});
  const [expandedSpecItems, setExpandedSpecItems] = useState({});
  const [imageLoadFailures, setImageLoadFailures] = useState({});
  const segs = Array.isArray(quote?.lineItems) ? quote.lineItems : [];
  const effectiveBusinessName =
    String(businessName || "").trim() ||
    String(quote?.quoteFrom?.name || "").trim() ||
    "FabriQuote";
  const effectiveBusinessAddress =
    String(shopAddress || "").trim() ||
    String(quote?.quoteFrom?.address || "").trim() ||
    "";
  const availableExecutionTemplates = (Array.isArray(executionTemplates)
    ? executionTemplates
    : []
  )
    .filter(
      (template) =>
        String(template?.name ?? "").trim() || String(template?.itemType ?? "").trim()
    )
    .sort((left, right) =>
      String(left?.name ?? "").localeCompare(String(right?.name ?? ""))
    );
  const normalizeSpecificationKey = (value = "") =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const handleImageOpenClick = (item, image, clickedUrl) => {
    console.log("GENERAL_QUOTE_IMAGE_OPEN_CLICK", {
      quoteNo: quote?.quoteNo || "",
      itemName: item?.itemName || item?.name || "",
      clickedUrl,
      resolvedDownloadURL: image?.downloadURL || "",
      legacyUrl: image?.legacyUrl || "",
      fullPath: image?.fullPath || image?.storagePath || image?.path || "",
      isOpeningResolvedUrl: clickedUrl === image?.downloadURL,
      hasToken: clickedUrl?.includes("token="),
    });
  };

  const getImageFailureKey = (item, image, displayUrl = "") =>
    `${item?.id || ""}:${image?.id || image?.fullPath || image?.storagePath || ""}:${displayUrl}`;

  const markImageLoadFailed = (item, image, displayUrl) => {
    const key = getImageFailureKey(item, image, displayUrl);
    setImageLoadFailures((prev) => ({
      ...prev,
      [key]: true,
    }));
    console.warn("GENERAL_QUOTE_IMAGE_LOAD_FAILED", {
      quoteNo: quote?.quoteNo || "",
      itemName: item?.itemName || item?.name || "",
      imageId: image?.id || "",
      displayUrl,
      fullPath: image?.fullPath || image?.storagePath || image?.path || "",
      legacyUrl: image?.legacyUrl || "",
      hasToken: displayUrl?.includes("token="),
    });
  };

  const renderUnavailableImageActions = (item, image, imageIndex, compact = false) => {
    const replacementInputId = `replace-general-item-image-${item?.id}-${imageIndex}`;
    const repairKey = `${item?.id || ""}:${image?.id || imageIndex}`;
    const repairState = imageRepairState?.[repairKey] || null;
    const candidateState = imageRepairCandidates?.[repairKey] || null;
    const candidates = Array.isArray(candidateState?.candidates)
      ? candidateState.candidates
      : [];
    const statusTextByState = {
      checking: "Checking exact path...",
      searching: "Searching legacy folders...",
      repaired: "Repaired",
      missing: "Image file was not found in Firebase Storage.",
      failed: "Repair failed",
    };
    const buttonStyle = {
      border: "none",
      borderRadius: 8,
      padding: compact ? "4px 6px" : "5px 7px",
      color: "white",
      fontSize: 10,
      fontWeight: 800,
      cursor: "pointer",
      lineHeight: 1.2,
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 5,
          alignItems: "center",
          marginTop: 6,
        }}
      >
        {repairState?.status ? (
          <div
            style={{
              width: "100%",
              padding: "5px 7px",
              borderRadius: 8,
              background:
                repairState.status === "repaired"
                  ? "rgba(22,163,74,0.16)"
                  : repairState.status === "missing" || repairState.status === "failed"
                  ? "rgba(220,38,38,0.14)"
                  : "rgba(37,99,235,0.14)",
              color:
                repairState.status === "repaired"
                  ? "#16a34a"
                  : repairState.status === "missing" || repairState.status === "failed"
                  ? "#dc2626"
                  : "#2563eb",
              fontSize: 10,
              fontWeight: 900,
              lineHeight: 1.25,
              boxSizing: "border-box",
            }}
          >
            {statusTextByState[repairState.status] || repairState.status}
            {repairState?.error && repairState.status === "failed" ? ` ${repairState.error}` : ""}
          </div>
        ) : null}
        {onRepairItemImage && canChangeImages ? (
          <button
            type="button"
            onClick={() => onRepairItemImage(item.id, image.id, imageIndex)}
            style={{
              ...buttonStyle,
              background: "#2563eb",
            }}
          >
            Repair This Image
          </button>
        ) : null}
        {onRetryItemImage ? (
          <button type="button" style={buttonStyle}
            onClick={() => onRetryItemImage(item.id, image.id)}>
            Retry Preview
          </button>
        ) : null}
        {onReplaceItemImage && canChangeImages ? (
          <>
            <label
              htmlFor={replacementInputId}
              style={{
                ...buttonStyle,
                background: "#0f766e",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Upload Replacement Image
            </label>
            <input
              id={replacementInputId}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: "none" }}
              onChange={async (event) => {
                const file = event.target.files?.[0] || null;
                if (file) {
                  await onReplaceItemImage(item.id, image.id, imageIndex, file, image);
                }
                event.target.value = "";
              }}
            />
          </>
        ) : null}
        {onCopyImageDebug ? (
          <button
            type="button"
            onClick={() => onCopyImageDebug(item.id, image.id, imageIndex, image)}
            style={{
              ...buttonStyle,
              background: isDark ? "#334155" : "#475569",
            }}
          >
            Copy Debug JSON
          </button>
        ) : null}
        {candidates.length ? (
          <div
            style={{
              width: "100%",
              marginTop: 2,
              padding: 8,
              borderRadius: 10,
              background: isDark ? "rgba(15,23,42,0.92)" : "rgba(255,255,255,0.94)",
              border: isDark
                ? "1px solid rgba(148,163,184,0.22)"
                : "1px solid rgba(203,213,225,0.9)",
              color: isDark ? "#e2e8f0" : "#0f172a",
              boxSizing: "border-box",
              display: "grid",
              gap: 6,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 900 }}>
              Possible Storage files
            </div>
            {candidates.slice(0, 5).map((candidate, candidateIndex) => (
              <div
                key={`${candidate.bucket || ""}:${candidate.fullPath || candidate.path || candidateIndex}`}
                style={{
                  display: "grid",
                  gap: 4,
                  padding: 6,
                  borderRadius: 8,
                  background: isDark ? "rgba(30,41,59,0.75)" : "#f8fafc",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    wordBreak: "break-word",
                  }}
                >
                  {candidate.filename ||
                    String(candidate.fullPath || candidate.path || "").split("/").pop() ||
                    `Candidate ${candidateIndex + 1}`}
                </div>
                <div
                  style={{
                    fontSize: 9,
                    opacity: 0.75,
                    wordBreak: "break-all",
                  }}
                >
                  {candidate.fullPath || candidate.path}
                </div>
                <div style={{ fontSize: 9, opacity: 0.75 }}>
                  {candidate.size ? `${Number(candidate.size).toLocaleString()} bytes` : "Size unknown"}
                  {candidate.updated ? ` | ${new Date(candidate.updated).toLocaleString()}` : ""}
                </div>
                {onSelectRepairCandidate ? (
                  <button
                    type="button"
                    onClick={() =>
                      onSelectRepairCandidate(item.id, image.id, imageIndex, candidate)
                    }
                    style={{
                      ...buttonStyle,
                      background: "#1d4ed8",
                      width: "100%",
                    }}
                  >
                    Use This File
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const updateQuote = (field, value) => {
    setQuote((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateNested = (section, field, value) => {
    setQuote((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [field]: value,
      },
    }));
  };

  const getEditableQuoteItems = (quoteLike) =>
    Array.isArray(quoteLike?.lineItems)
      ? quoteLike.lineItems
      : Array.isArray(quoteLike?.items)
      ? quoteLike.items
      : [];

  const syncQuoteItems = (prev, lineItems) => ({
    ...prev,
    lineItems,
    items: lineItems,
    products: lineItems,
    rows: lineItems,
    generalRows: lineItems,
    quoteRows: lineItems,
    generalItems: lineItems,
  });

  const updateItem = (itemId, field, value) => {
    setQuote((prev) => {
      const lineItems = getEditableQuoteItems(prev).map((item) =>
        item.id === itemId
          ? {
              ...item,
              [field]: value,
              ...(field === "name" || field === "itemName"
                ? { name: value, itemName: value }
                : {}),
              ...(field === "productUrl" ? { imageLinkUrl: value } : {}),
              ...(field === "imageLinkUrl" ? { productUrl: value } : {}),
            }
          : item
      );

      return syncQuoteItems(prev, lineItems);
    });
  };

  const updateItemSpecifications = (itemId, updater) => {
    setQuote((prev) => {
      const lineItems = getEditableQuoteItems(prev).map((item) => {
        if (item.id !== itemId) return item;

        const currentSpecifications = Array.isArray(item.specifications)
          ? item.specifications
          : [];
        const nextSpecifications =
          typeof updater === "function"
            ? updater(currentSpecifications, item)
            : updater;

        return {
          ...item,
          specifications: Array.isArray(nextSpecifications)
            ? nextSpecifications.map((specification) =>
                createGeneralQuoteSpecification(specification)
              )
            : [],
          isTemplateModified:
            item?.specTemplateId || item?.specTemplateName
              ? true
              : item?.isTemplateModified === true,
        };
      });

      return syncQuoteItems(prev, lineItems);
    });
  };

  const toggleSpecsExpanded = (itemId) => {
    setExpandedSpecItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const addSpecificationField = (itemId) => {
    setExpandedSpecItems((prev) => ({ ...prev, [itemId]: true }));
    updateItemSpecifications(itemId, (specifications) => [
      ...(Array.isArray(specifications) ? specifications : []),
      createGeneralQuoteSpecification({
        key: "New Field",
        value: "",
        type: "text",
      }),
    ]);
  };

  const updateSpecificationField = (itemId, specIndex, patch) => {
    updateItemSpecifications(itemId, (specifications) =>
      (Array.isArray(specifications) ? specifications : []).map(
        (specification, index) =>
          index === specIndex
            ? {
                ...specification,
                ...patch,
              }
            : specification
      )
    );
  };

  const removeSpecificationField = (itemId, specIndex) => {
    updateItemSpecifications(itemId, (specifications) =>
      (Array.isArray(specifications) ? specifications : []).filter(
        (_, index) => index !== specIndex
      )
    );
  };

  const clearSpecificationTemplate = (itemId) => {
    setQuote((prev) => {
      const lineItems = getEditableQuoteItems(prev).map((item) =>
        item.id === itemId
          ? {
              ...item,
              specTemplateId: null,
              specTemplateName: "",
              specItemType: "",
              isTemplateModified: false,
            }
          : item
      );

      return syncQuoteItems(prev, lineItems);
    });
  };

  const applySpecificationTemplate = (itemId, templateId) => {
    const template =
      (Array.isArray(executionTemplates) ? executionTemplates : []).find(
        (entry) => entry.id === templateId
      ) || null;

    if (!template) return;

    setExpandedSpecItems((prev) => ({ ...prev, [itemId]: true }));
    setQuote((prev) => {
      const lineItems = getEditableQuoteItems(prev).map((item) => {
        if (item.id !== itemId) return item;

        const currentSpecifications = normalizeGeneralQuoteItemSpecifications(item);
        const existingByKey = new Map(
          currentSpecifications.map((specification, index) => [
            normalizeSpecificationKey(specification?.key),
            index,
          ])
        );
        const nextSpecifications = [...currentSpecifications];

        (Array.isArray(template.fields) ? template.fields : []).forEach((field) => {
          const nextKey = String(field?.key ?? "").trim();
          if (!nextKey) return;

          const normalizedKey = normalizeSpecificationKey(nextKey);
          const existingIndex = existingByKey.get(normalizedKey);
          const defaultValue =
            field?.defaultValue == null ? "" : String(field.defaultValue);

          if (existingIndex == null) {
            nextSpecifications.push(
              createGeneralQuoteSpecification({
                key: nextKey,
                value: defaultValue,
              })
            );
            existingByKey.set(normalizedKey, nextSpecifications.length - 1);
            return;
          }

          const existingSpecification = nextSpecifications[existingIndex];
          if (!String(existingSpecification?.value ?? "").trim() && defaultValue) {
            nextSpecifications[existingIndex] = createGeneralQuoteSpecification({
              key: existingSpecification?.key ?? nextKey,
              value: defaultValue,
            });
          }
        });

        return {
          ...item,
          specTemplateId: template.id,
          specTemplateName: template.name || "",
          specItemType: template.itemType || "",
          isTemplateModified: false,
          specifications: nextSpecifications,
        };
      });

      return syncQuoteItems(prev, lineItems);
    });
  };

  const resetSpecificationTemplate = (itemId) => {
    const template =
      (Array.isArray(executionTemplates) ? executionTemplates : []).find((entry) =>
        entry.id &&
        entry.id ===
          getEditableQuoteItems(quote).find((item) => item.id === itemId)?.specTemplateId
      ) || null;

    if (!template) return;

    setExpandedSpecItems((prev) => ({ ...prev, [itemId]: true }));
    setQuote((prev) => {
      const lineItems = getEditableQuoteItems(prev).map((item) =>
        item.id === itemId
          ? {
              ...item,
              specTemplateId: template.id,
              specTemplateName: template.name || "",
              specItemType: template.itemType || "",
              isTemplateModified: false,
              specifications: (Array.isArray(template.fields) ? template.fields : [])
                .map((field) =>
                  createGeneralQuoteSpecification({
                    key: field?.key ?? "",
                    value: field?.defaultValue ?? "",
                  })
                )
                .filter(
                  (specification) =>
                    String(specification?.key ?? "").trim() ||
                    String(specification?.value ?? "").trim()
                ),
            }
          : item
      );

      return syncQuoteItems(prev, lineItems);
    });
  };

  const toggleDescription = (itemId) => {
    setQuote((prev) => {
      const lineItems = getEditableQuoteItems(prev).map((item) =>
        item.id === itemId
          ? {
              ...item,
              showDescription: !item.showDescription,
            }
          : item
      );

      return syncQuoteItems(prev, lineItems);
    });
  };

  const moveItemImage = (itemId, imageId, direction) => {
    setQuote((prev) => {
      const lineItems = getEditableQuoteItems(prev).map((item) => {
        if (item.id !== itemId) return item;

        const images = [...normalizeGeneralQuoteItemImages(item)];
        const currentIndex = images.findIndex((image) => image.id === imageId);
        const targetIndex = currentIndex + direction;

        if (
          currentIndex < 0 ||
          targetIndex < 0 ||
          targetIndex >= images.length
        ) {
          return item;
        }

        const [movedImage] = images.splice(currentIndex, 1);
        images.splice(targetIndex, 0, movedImage);

        return syncGeneralQuoteItemPrimaryImageFields({
          ...item,
          images,
        });
      });

      return syncQuoteItems(prev, lineItems);
    });
  };

  const addItem = () => {
    const newItem = createGeneralQuoteItem();
    setQuote((prev) =>
      syncQuoteItems(prev, [...getEditableQuoteItems(prev), newItem])
    );
    setExpandedItems((prev) => ({
      ...prev,
      [newItem.id]: true,
    }));
  };

  const insertItemAt = (itemIndex, position = "below") => {
    const newItem = createGeneralQuoteItem();

    setQuote((prev) => {
      const items = [...getEditableQuoteItems(prev)];
      const insertIndex =
        position === "above"
          ? Math.max(0, itemIndex)
          : Math.min(items.length, itemIndex + 1);

      items.splice(insertIndex, 0, newItem);

      return syncQuoteItems(prev, items);
    });

    setExpandedItems((prev) => ({
      ...prev,
      [newItem.id]: true,
    }));
  };

  const toggleItemExpanded = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  return (
    <div style={{ display: "grid", gap: 20 }} onBlurCapture={onDraftBlur}>
      <div
        style={{
          ...cardStyle(isDark),
          padding: 18,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: "-0.04em",
              color: isDark ? "#f8fafc" : "#0f172a",
            }}
          >
            General Quotation
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 13,
              color: isDark ? "#94a3b8" : "#64748b",
            }}
          >
            Premium quotation builder for furniture, blinds, wallpaper, services,
            and custom products.
          </div>
          <div
            style={{
              marginTop: 10,
              display: "inline-flex",
              alignItems: "center",
              borderRadius: 999,
              padding: "6px 10px",
              background:
                editorMode === "editing_existing"
                  ? isDark
                    ? "rgba(37,99,235,0.2)"
                    : "#dbeafe"
                  : isDark
                  ? "rgba(22,163,74,0.18)"
                  : "#dcfce7",
              color:
                editorMode === "editing_existing"
                  ? isDark
                    ? "#bfdbfe"
                    : "#1d4ed8"
                  : isDark
                  ? "#bbf7d0"
                  : "#166534",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {editorMode === "editing_existing"
              ? `Editing ${editingQuoteLabel || "saved quotation"}`
              : "New quotation"}
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {onStartNewQuote ? (
            <Button
              type="button"
              icon={Plus}
              variant="ghost"
              onClick={(event) => runProtectedQuoteAction(event, onStartNewQuote)}
              disabled={isSaving || hasUploadInProgress || !canStartNewQuote}
            >
              New Quote
            </Button>
          ) : null}
          <Button
            type="button"
            icon={Save}
            variant="success"
            onClick={(event) => runProtectedQuoteAction(event, onSave)}
            disabled={isSaving || hasUploadInProgress || !canSave}
          >
            {isSaving ? "Saving..." : hasUploadInProgress ? "Uploading..." : "Save"}
          </Button>
          <Button
            type="button"
            icon={Save}
            variant="primary"
            onClick={(event) => runProtectedQuoteAction(event, onSaveAsNew)}
            disabled={isSaving || hasUploadInProgress || !canSaveAsNew}
          >
            Save As New
          </Button>
          <Button
            type="button"
            icon={Download}
            variant="primary"
            onClick={(event) => runProtectedQuoteAction(event, onExportPdf)}
            disabled={!canExportPdf}
          >
            Export PDF
          </Button>
          {onExportExecutionPdf ? (
            <Button
              type="button"
              icon={ShieldCheck}
              variant="secondary"
              onClick={(event) =>
                runProtectedQuoteAction(event, onExportExecutionPdf)
              }
              disabled={!canExportExecutionPdf}
            >
              Execution PDF
            </Button>
          ) : null}
        </div>
      </div>

      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color:
            autoSaveStatus === "saving"
              ? "#2563eb"
              : autoSaveStatus === "saved"
              ? "#16a34a"
              : autoSaveStatus === "error"
              ? "#dc2626"
              : localDraftStatus?.state === "dirty"
              ? isDark
                ? "#fbbf24"
                : "#b45309"
              : localDraftStatus?.state === "autosaved"
              ? "#16a34a"
              : isDark
              ? "#94a3b8"
              : "#64748b",
        }}
      >
        {autoSaveStatus === "saving" && "Saving quotation..."}
        {autoSaveStatus === "saved" && "All changes saved"}
        {autoSaveStatus === "error" && "Save failed"}
        {autoSaveStatus === "idle" && localDraftStatus?.state === "dirty" && "Unsaved changes"}
        {autoSaveStatus === "idle" &&
          localDraftStatus?.state === "autosaved" &&
          `Autosaved locally${
            localDraftStatus?.savedAt
              ? ` at ${new Date(localDraftStatus.savedAt).toLocaleTimeString("en-IN", {
                  hour: "numeric",
                  minute: "2-digit",
                })}`
              : ""
          }`}
        {autoSaveStatus === "idle" &&
          localDraftStatus?.state === "saved" &&
          "Saved"}
        {autoSaveStatus === "idle" &&
          (!localDraftStatus || localDraftStatus.state === "idle") &&
          "Ready"}
      </div>

      {validationError ? (
        <div
          role="alert"
          style={{
            borderRadius: 16,
            border: isDark ? "1px solid rgba(248,113,113,0.4)" : "1px solid #fecaca",
            background: isDark ? "rgba(127,29,29,0.34)" : "#fef2f2",
            color: isDark ? "#fecaca" : "#991b1b",
            padding: "12px 14px",
            fontSize: 13,
            fontWeight: 800,
            lineHeight: 1.45,
          }}
        >
          <div>{validationError.message}</div>
          <div style={{ marginTop: 4, opacity: 0.78 }}>
            Field: {validationError.failedFieldLabel || validationError.failedField || "Field"}
            {" "}· Current value: {String(validationError.failedValue ?? "") || "empty"}
          </div>
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gap: 20,
          alignItems: "start",
        }}
      >
        <div style={{ display: "grid", gap: 20 }}>
          <div style={cardStyle(isDark)}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isCompact
                  ? "1fr"
                  : "minmax(0, 1.2fr) minmax(240px, 0.8fr)",
                gap: 20,
                alignItems: "center",
              }}
            >
              <div style={{ display: "grid", gap: 16 }}>
                <div>
                  <label style={fieldLabelStyle(isDark)}>Quotation Title</label>
                  <input
                    style={fieldStyle(isDark)}
                    value={quote.title || ""}
                    onChange={(e) => updateQuote("title", e.target.value)}
                    placeholder="Quotation"
                  />
                </div>

                <div>
                  <label style={fieldLabelStyle(isDark)}>Subtitle</label>
                  <input
                    style={fieldStyle(isDark)}
                    value={quote.subtitle || ""}
                    onChange={(e) => updateQuote("subtitle", e.target.value)}
                    placeholder="Project summary, room name, or product collection"
                  />
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(3, minmax(0, 1fr))",
                    gap: 14,
                  }}
                >
                  <div>
                    <label style={fieldLabelStyle(isDark)}>Quotation No.</label>
                    <input
                      style={fieldStyle(isDark)}
                      value={quote.quoteNo || ""}
                      onChange={(e) => updateQuote("quoteNo", e.target.value)}
                      placeholder="Generated on save"
                    />
                  </div>

                  <div>
                    <label style={fieldLabelStyle(isDark)}>Quotation Date</label>
                    <input
                      type="date"
                      style={fieldStyle(isDark)}
                      value={quote.quoteDate || ""}
                      onChange={(e) => updateQuote("quoteDate", e.target.value)}
                    />
                  </div>

                  <div>
                    <label style={fieldLabelStyle(isDark)}>Valid Till</label>
                    <input
                      type="date"
                      style={fieldStyle(isDark)}
                      value={quote.validTill || ""}
                      onChange={(e) => updateQuote("validTill", e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div
                style={{
                  borderRadius: 18,
                  padding: 20,
                  background: isDark ? "#020617" : "#f8fafc",
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.16)"
                    : "1px solid #e2e8f0",
                  minHeight: 200,
                  display: "grid",
                  alignContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {logoURL ? (
                    <img
                      src={logoURL}
                      alt="Business logo"
                      style={{
                        width: 76,
                        height: 76,
                        objectFit: "contain",
                        borderRadius: 16,
                        background: "#ffffff",
                        padding: 10,
                      }}
                    />
                  ) : (
                    <div style={iconBadgeStyle(isDark)}>
                      <FileText size={18} />
                    </div>
                  )}

                  <div>
                    <div
                      style={{
                        fontSize: 17,
                        fontWeight: 700,
                        color: isDark ? "#f8fafc" : "#0f172a",
                      }}
                    >
                      {effectiveBusinessName || "Business Details"}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: isDark ? "#94a3b8" : "#64748b",
                        marginTop: 4,
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {effectiveBusinessAddress || "Business address will appear here."}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gap: 8,
                    marginTop: 16,
                    fontSize: 13,
                    color: isDark ? "#cbd5e1" : "#475569",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CalendarDays size={15} />
                    <span>Prepared on {quote.quoteDate || "-"}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <ReceiptText size={15} />
                    <span>{quote.quoteNo || "Quotation number will be assigned on save"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isCompact
                ? "1fr"
                : "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 20,
            }}
          >
            <div style={cardStyle(isDark)}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
                <div style={iconBadgeStyle(isDark)}>
                  <Building2 size={18} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: isDark ? "#f8fafc" : "#0f172a",
                    }}
                  >
                    Quotation From
                  </div>
                  <div style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b" }}>
                    Business details from Settings
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gap: 14 }}>
                <div>
                  <label style={fieldLabelStyle(isDark)}>Business Name</label>
                  <input
                    style={fieldStyle(isDark)}
                    value={effectiveBusinessName}
                    readOnly
                  />
                </div>
                <div>
                  <label style={fieldLabelStyle(isDark)}>Business Address</label>
                  <textarea
                    style={fieldStyle(isDark, true)}
                    value={effectiveBusinessAddress}
                    readOnly
                  />
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(2, minmax(0, 1fr))",
                    gap: 14,
                  }}
                >
                  <div>
                    <label style={fieldLabelStyle(isDark)}>Phone</label>
                    <input
                      style={fieldStyle(isDark)}
                      value={quote.quoteFrom?.phone || ""}
                      onChange={(e) => updateNested("quoteFrom", "phone", e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={fieldLabelStyle(isDark)}>Email</label>
                    <input
                      style={fieldStyle(isDark)}
                      value={quote.quoteFrom?.email || ""}
                      onChange={(e) => updateNested("quoteFrom", "email", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div style={cardStyle(isDark)}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
                <div style={iconBadgeStyle(isDark)}>
                  <UserRound size={18} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: isDark ? "#f8fafc" : "#0f172a",
                    }}
                  >
                    Quotation For
                  </div>
                  <div style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b" }}>
                    Client details
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gap: 14 }}>
                {customerSelector || (
                  <div>
                    <label style={fieldLabelStyle(isDark)}>Contact Name</label>
                    <input
                      style={fieldStyle(isDark)}
                      value={quote.quoteFor?.name || ""}
                      onChange={(e) => updateNested("quoteFor", "name", e.target.value)}
                    />
                  </div>
                )}
                {siteSelector}
                {referralPartnerSelector}
                <div>
                  <label style={fieldLabelStyle(isDark)}>Company / Project</label>
                  <input
                    style={fieldStyle(isDark)}
                    value={quote.quoteFor?.company || ""}
                    onChange={(e) => updateNested("quoteFor", "company", e.target.value)}
                  />
                </div>
                <div>
                  <label style={fieldLabelStyle(isDark)}>Address</label>
                  <textarea
                    style={fieldStyle(isDark, true)}
                    value={quote.quoteFor?.address || ""}
                    onChange={(e) => updateNested("quoteFor", "address", e.target.value)}
                  />
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(2, minmax(0, 1fr))",
                    gap: 14,
                  }}
                >
                  <div>
                    <label style={fieldLabelStyle(isDark)}>Phone</label>
                    <input
                      style={fieldStyle(isDark)}
                      value={quote.quoteFor?.phone || ""}
                      onChange={(e) => updateNested("quoteFor", "phone", e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={fieldLabelStyle(isDark)}>Email</label>
                    <input
                      style={fieldStyle(isDark)}
                      value={quote.quoteFor?.email || ""}
                      onChange={(e) => updateNested("quoteFor", "email", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={cardStyle(isDark)}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 12,
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: isDark ? "#f8fafc" : "#0f172a",
                  }}
                >
                  Line Items
                </div>
                <div style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", marginTop: 4 }}>
                  Add products, services, custom materials, or installation lines.
                </div>
              </div>

              <Button icon={Plus} variant="primary" onClick={addItem}>
                Add Line Item
              </Button>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
              {segs.map((item, index) => {
                const itemAmount = getGeneralItemAmount(item);
                const itemTaxAmount = getGeneralItemTaxAmount(item);
                const itemImages = normalizeGeneralQuoteItemImages(item);
                const itemSpecifications = normalizeGeneralQuoteItemSpecifications(item);
                const specificationCount = itemSpecifications.length;
                const isSpecsExpanded = expandedSpecItems[item.id] === true;
                const activeTemplate =
                  availableExecutionTemplates.find(
                    (template) => template.id === item.specTemplateId
                  ) || null;
                const templateDisplayName =
                  item.specTemplateName ||
                  activeTemplate?.name ||
                  "";
                const quickSpecPreview = itemSpecifications
                  .slice(0, 3)
                  .map((specification) =>
                    String(specification?.value ?? "").trim() ||
                    String(specification?.key ?? "").trim()
                  )
                  .filter(Boolean)
                  .join(" • ");
                const isExecutionLocked = item.isExecutionLocked === true;
                const primaryImage = itemImages[0] || null;
                const primaryImageUrl = getImageDisplayUrl(primaryImage);
                const rawPreviewLink = item.imageLinkUrl || item.productUrl || "";
                const safePreviewLink = isFirebaseStorageRestUrl(rawPreviewLink)
                  ? ""
                  : rawPreviewLink;
                const previewLink =
                  safePreviewLink ||
                  primaryImageUrl ||
                  "";
                const isExpanded = !isCompact || expandedItems[item.id] || index === 0;
                const itemValidationMatches =
                  validationError &&
                  (String(validationError.rowId || "") === String(item.id || "") ||
                    Number(validationError.rowIndex) === index);

                return (
                  <div
                    key={item.id}
                    data-quote-row-id={item.id}
                    data-quote-row-index={index}
                    style={{
                      borderRadius: 18,
                      border: itemValidationMatches
                        ? "2px solid #dc2626"
                        : isDark
                        ? "1px solid rgba(148,163,184,0.16)"
                        : "1px solid #e2e8f0",
                      background: isDark ? "#020617" : "#f8fafc",
                      padding: 18,
                      display: "grid",
                      gap: 14,
                      boxShadow: itemValidationMatches
                        ? isDark
                          ? "0 16px 34px rgba(127,29,29,0.22)"
                          : "0 16px 34px rgba(220,38,38,0.12)"
                        : "none",
                    }}
                  >
                    {itemValidationMatches ? (
                      <div
                        role="alert"
                        style={{
                          borderRadius: 14,
                          border: isDark
                            ? "1px solid rgba(248,113,113,0.4)"
                            : "1px solid #fecaca",
                          background: isDark ? "rgba(127,29,29,0.34)" : "#fef2f2",
                          color: isDark ? "#fecaca" : "#991b1b",
                          padding: "10px 12px",
                          fontSize: 12.5,
                          fontWeight: 800,
                          lineHeight: 1.45,
                        }}
                      >
                        <div>{validationError.message}</div>
                        <div style={{ marginTop: 4, opacity: 0.78 }}>
                          Field: {validationError.failedFieldLabel || validationError.failedField || "Field"}
                          {" "}· Current value: {String(validationError.failedValue ?? "") || "empty"}
                        </div>
                      </div>
                    ) : null}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 12,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => isCompact && toggleItemExpanded(item.id)}
                        style={{
                          border: "none",
                          background: "transparent",
                          padding: 0,
                          margin: 0,
                          cursor: isCompact ? "pointer" : "default",
                          textAlign: "left",
                          flex: 1,
                          display: "grid",
                          gap: 6,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 800,
                            color: isDark ? "#f8fafc" : "#0f172a",
                          }}
                        >
                          {item.itemName || item.name || `Item ${index + 1}`}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 10,
                            fontSize: 12,
                            color: isDark ? "#94a3b8" : "#64748b",
                          }}
                        >
                          <span>Qty {Number(item.quantity || 0).toFixed(2)}</span>
                          <span>Rate {currencySymbol} {Number(item.rate || 0).toFixed(2)}</span>
                          <span>Amount {currencySymbol} {itemAmount.toFixed(2)}</span>
                          <span>
                            GST {Number(item.gstPercent || 0)}%{" "}
                            {item.gstMode === "exclusive" ? "Exclusive" : "Inclusive"}
                          </span>
                          <span>
                            {specificationCount} spec{specificationCount === 1 ? "" : "s"} added
                          </span>
                          {templateDisplayName ? (
                            <span>Template {templateDisplayName}</span>
                          ) : null}
                        </div>
                      </button>

                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent: "flex-end",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <Button
                          icon={Plus}
                          variant="ghost"
                          onClick={() => insertItemAt(index, "above")}
                          style={{ padding: "10px 12px" }}
                        >
                          Add Above
                        </Button>
                        <Button
                          icon={Plus}
                          variant="primary"
                          onClick={() => insertItemAt(index, "below")}
                          style={{ padding: "10px 12px" }}
                        >
                          Add Below
                        </Button>
                        <Button
                          icon={Trash2}
                          variant="danger"
                          onClick={() => onRemoveItem(item.id)}
                          disabled={segs.length === 1}
                          style={{ padding: "10px 12px" }}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>

                    {isExpanded ? (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isCompact
                          ? "1fr"
                          : "112px minmax(0, 1fr)",
                        gap: 16,
                        alignItems: "start",
                      }}
                    >
                      <div
                        style={{
                          width: isCompact ? "100%" : 112,
                          minHeight: 112,
                          borderRadius: 18,
                          border: isDark
                            ? "1px solid rgba(148,163,184,0.16)"
                            : "1px solid #dbe4f0",
                          background: isDark ? "#0f172a" : "#ffffff",
                          display: "grid",
                          gridTemplateColumns:
                            itemImages.length > 1
                              ? "repeat(2, minmax(0, 1fr))"
                              : "1fr",
                          gap: 6,
                          overflow: "hidden",
                          padding: 8,
                          boxSizing: "border-box",
                        }}
                      >
                        {itemImages.length > 0 ? (
                          itemImages.slice(0, 4).map((image, imageIndex) => {
                            const displayUrl = getImageDisplayUrl(image);
                            const fullImageUrl = getImageFullUrl(image) || displayUrl;
                            const imageRepairKey = `${item?.id || ""}:${image?.id || imageIndex}`;
                            const repairState = imageRepairState?.[imageRepairKey] || null;
                            const imageFailed =
                              displayUrl &&
                              imageLoadFailures[getImageFailureKey(item, image, displayUrl)];
                            const usableDisplayUrl = imageFailed ? "" : displayUrl;
                            const tileStyle = {
                              width: "100%",
                              minHeight: itemImages.length > 1 ? 44 : 96,
                              borderRadius: 12,
                              overflow: "hidden",
                              position: "relative",
                              display: "block",
                            };
                            return usableDisplayUrl ? (
                              <a
                                key={image.id}
                                href={fullImageUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => handleImageOpenClick(item, image, fullImageUrl)}
                                style={tileStyle}
                              >
                                <img
                                  src={usableDisplayUrl}
                                  alt={item.name || `Item ${index + 1}`}
                                  onError={() =>
                                    markImageLoadFailed(item, image, usableDisplayUrl)
                                  }
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    minHeight: itemImages.length > 1 ? 44 : 96,
                                  }}
                                />
                              </a>
                            ) : (
                              <div
                                key={image.id}
                                title={getImageUnavailableBody(image, repairState)}
                                style={{
                                  ...tileStyle,
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  textAlign: "center",
                                  color: isDark ? "#cbd5e1" : "#475569",
                                  background: isDark ? "#111827" : "#f1f5f9",
                                  fontSize: 10,
                                  lineHeight: 1.3,
                                  padding: 6,
                                  boxSizing: "border-box",
                                }}
                              >
                                <span style={{ fontWeight: 900 }}>Image unavailable</span>
                                <span>{getImageUnavailableBody(image, repairState)}</span>
                                {renderUnavailableImageActions(item, image, imageIndex, true)}
                              </div>
                            );
                          })
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              minHeight: 96,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <ImagePlus size={20} color={isDark ? "#94a3b8" : "#94a3b8"} />
                          </div>
                        )}
                      </div>

                      <div style={{ display: "grid", gap: 14 }}>
                        <div>
                          <label style={fieldLabelStyle(isDark)}>Item Name</label>
                          <input
                            style={fieldStyle(isDark)}
                            value={item.itemName ?? item.name ?? ""}
                            onChange={(e) => updateItem(item.id, "name", e.target.value)}
                            placeholder="Sofa, wallpaper panel, blind, service package"
                          />
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isCompact
                              ? "1fr"
                              : "repeat(3, minmax(0, 1fr))",
                            gap: 12,
                          }}
                        >
                          <div>
                            <label style={fieldLabelStyle(isDark)}>Room / Area</label>
                            <input
                              style={fieldStyle(isDark)}
                              value={item.roomName ?? ""}
                              onChange={(e) =>
                                updateItem(item.id, "roomName", e.target.value)
                              }
                              placeholder="Living room"
                            />
                          </div>
                          <div>
                            <label style={fieldLabelStyle(isDark)}>Section</label>
                            <input
                              style={fieldStyle(isDark)}
                              value={item.areaName ?? ""}
                              onChange={(e) =>
                                updateItem(item.id, "areaName", e.target.value)
                              }
                              placeholder="TV wall, bay window..."
                            />
                          </div>
                          <div>
                            <label style={fieldLabelStyle(isDark)}>Window / Label</label>
                            <input
                              style={fieldStyle(isDark)}
                              value={item.windowLabel ?? ""}
                              onChange={(e) =>
                                updateItem(item.id, "windowLabel", e.target.value)
                              }
                              placeholder="W1, panel A..."
                            />
                          </div>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isCompact
                              ? "repeat(2, minmax(0, 1fr))"
                              : "minmax(110px, 0.7fr) minmax(100px, 0.6fr) minmax(110px, 0.7fr) minmax(120px, 0.7fr) minmax(120px, 0.9fr) minmax(120px, 1fr)",
                            gap: 12,
                          }}
                        >
                          <div>
                            <label style={fieldLabelStyle(isDark)}>Quantity</label>
                            <input
                              type="number"
                              style={fieldStyle(isDark)}
                              value={item.quantity ?? ""}
                              aria-label="Quantity"
                              onChange={(e) => updateItem(item.id, "quantity", e.target.value)}
                            />
                          </div>
                          <div>
                            <label style={fieldLabelStyle(isDark)}>Unit</label>
                            <input
                              style={fieldStyle(isDark)}
                              value={item.unit || ""}
                              onChange={(e) => updateItem(item.id, "unit", e.target.value)}
                              placeholder="pcs"
                            />
                          </div>
                          <div>
                            <label style={fieldLabelStyle(isDark)}>Rate</label>
                            <input
                              type="number"
                              style={fieldStyle(isDark)}
                              value={item.rate ?? ""}
                              aria-label="Rate"
                              onChange={(e) => updateItem(item.id, "rate", e.target.value)}
                            />
                          </div>
                          <div>
                            <label style={fieldLabelStyle(isDark)}>GST %</label>
                            <select
                              style={fieldStyle(isDark)}
                              value={Number(item.gstPercent || 0)}
                              onChange={(e) =>
                                updateItem(item.id, "gstPercent", Number(e.target.value))
                              }
                            >
                              <option value={0}>0%</option>
                              <option value={5}>5%</option>
                              <option value={12}>12%</option>
                              <option value={18}>18%</option>
                            </select>
                          </div>
                          <div>
                            <label style={fieldLabelStyle(isDark)}>Pricing Mode</label>
                            <select
                              style={fieldStyle(isDark)}
                              value={item.gstMode || "inclusive"}
                              onChange={(e) => updateItem(item.id, "gstMode", e.target.value)}
                            >
                              <option value="inclusive">Inclusive</option>
                              <option value="exclusive">Exclusive</option>
                            </select>
                          </div>
                          <div>
                            <label style={fieldLabelStyle(isDark)}>Amount</label>
                            <div
                              style={{
                                ...fieldStyle(isDark),
                                display: "grid",
                                gap: 4,
                                fontWeight: 700,
                              }}
                            >
                              <span>
                                {currencySymbol} {itemAmount.toFixed(2)}
                              </span>
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: isDark ? "#94a3b8" : "#64748b",
                                }}
                              >
                                GST {Number(item.gstPercent || 0)}%{" "}
                                {item.gstMode === "exclusive" ? "added" : "included"}{" "}
                                ({currencySymbol} {itemTaxAmount.toFixed(2)})
                              </span>
                            </div>
                          </div>
                        </div>

                        <div
                          style={{
                            borderRadius: 20,
                            border: isDark
                              ? "1px solid rgba(148,163,184,0.16)"
                              : "1px solid rgba(226,232,240,0.9)",
                            background: isDark
                              ? "linear-gradient(180deg, rgba(15,23,42,0.9), rgba(2,6,23,0.88))"
                              : "linear-gradient(180deg, #f8fafc, #ffffff)",
                            padding: 16,
                            display: "grid",
                            gap: 14,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: 12,
                              flexWrap: "wrap",
                            }}
                          >
                            <div style={{ display: "grid", gap: 6 }}>
                              <div
                                style={{
                                  fontSize: 15,
                                  fontWeight: 800,
                                  color: isDark ? "#f8fafc" : "#0f172a",
                                }}
                              >
                                Specifications
                              </div>
                              <div
                                style={{
                                  fontSize: 13,
                                  color: isDark ? "#94a3b8" : "#64748b",
                                  lineHeight: 1.6,
                                }}
                              >
                                {specificationCount > 0
                                  ? `${specificationCount} structured spec${
                                      specificationCount === 1 ? "" : "s"
                                    } added${templateDisplayName ? ` via ${templateDisplayName}` : ""}.`
                                  : "Keep pricing clean while storing configurable product details in a separate, premium specs panel."}
                                {item.isTemplateModified && templateDisplayName ? (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      marginTop: 8,
                                      padding: "5px 10px",
                                      borderRadius: 999,
                                      background: isDark ? "rgba(245,158,11,0.14)" : "#fffbeb",
                                      border: "1px solid rgba(245,158,11,0.18)",
                                      color: isDark ? "#fde68a" : "#92400e",
                                      fontSize: 11,
                                      fontWeight: 700,
                                    }}
                                  >
                                    Customized from template
                                  </span>
                                ) : null}
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() => toggleSpecsExpanded(item.id)}
                              style={{
                                border: "none",
                                background: isDark ? "#111827" : "#e2e8f0",
                                color: isDark ? "#f8fafc" : "#0f172a",
                                borderRadius: 999,
                                padding: "8px 12px",
                                fontWeight: 700,
                                fontSize: 13,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                cursor: "pointer",
                              }}
                            >
                              {isSpecsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              {isSpecsExpanded ? "Collapse" : "Open Specs"}
                            </button>
                          </div>

                          {!isSpecsExpanded ? (
                            specificationCount > 0 ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 10,
                                }}
                              >
                                {quickSpecPreview ? (
                                  <div
                                    style={{
                                      width: "100%",
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: isDark ? "#cbd5e1" : "#334155",
                                    }}
                                  >
                                    {quickSpecPreview}
                                  </div>
                                ) : null}
                                {itemSpecifications.slice(0, 4).map((specification, specIndex) => (
                                  <div
                                    key={`${item.id}-spec-preview-${specIndex}`}
                                    style={{
                                      padding: "9px 12px",
                                      borderRadius: 999,
                                      background: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
                                      border: isDark
                                        ? "1px solid rgba(59,130,246,0.18)"
                                        : "1px solid rgba(191,219,254,0.9)",
                                      fontSize: 12,
                                      color: isDark ? "#dbeafe" : "#1e40af",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 6,
                                      maxWidth: "100%",
                                    }}
                                  >
                                    <span style={{ fontWeight: 800 }}>
                                      {specification.key || "Field"}
                                    </span>
                                    <span style={{ opacity: 0.78 }}>
                                      {specification.value || "-"}
                                    </span>
                                  </div>
                                ))}
                                {specificationCount > 4 ? (
                                  <div
                                    style={{
                                      padding: "9px 12px",
                                      borderRadius: 999,
                                      background: isDark ? "#111827" : "#e2e8f0",
                                      fontSize: 12,
                                      fontWeight: 700,
                                      color: isDark ? "#cbd5e1" : "#334155",
                                    }}
                                  >
                                    +{specificationCount - 4} more
                                  </div>
                                ) : null}
                              </div>
                            ) : (
                              <div
                                style={{
                                  padding: 14,
                                  borderRadius: 16,
                                  background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                                  border: isDark
                                    ? "1px solid rgba(148,163,184,0.12)"
                                    : "1px solid rgba(226,232,240,0.85)",
                                  fontSize: 13,
                                  color: isDark ? "#94a3b8" : "#64748b",
                                }}
                              >
                                No specifications added yet.
                              </div>
                            )
                          ) : (
                            <div style={{ display: "grid", gap: 14 }}>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: isCompact
                                    ? "1fr"
                                    : "minmax(0, 1fr) auto auto",
                                  gap: 12,
                                  alignItems: "end",
                                }}
                              >
                                <div>
                                  <label style={fieldLabelStyle(isDark)}>Apply Template</label>
                                  <select
                                    style={fieldStyle(isDark)}
                                    value={item.specTemplateId ?? ""}
                                    onChange={(e) => {
                                      const nextTemplateId = e.target.value;
                                      if (!nextTemplateId) {
                                        clearSpecificationTemplate(item.id);
                                        return;
                                      }
                                      applySpecificationTemplate(item.id, nextTemplateId);
                                    }}
                                    disabled={availableExecutionTemplates.length === 0 || isExecutionLocked}
                                  >
                                    <option value="">
                                      {availableExecutionTemplates.length === 0
                                        ? "No execution templates available"
                                        : "Select a template..."}
                                    </option>
                                    {availableExecutionTemplates.map((template) => (
                                      <option key={template.id} value={template.id}>
                                        {template.name} | {template.itemType}
                                      </option>
                                    ))}
                                  </select>
                                </div>

                                <Button
                                  icon={Plus}
                                  variant="ghost"
                                  onClick={() => addSpecificationField(item.id)}
                                  disabled={isExecutionLocked}
                                  style={{ padding: "11px 14px" }}
                                >
                                  Add Field
                                </Button>

                                <Button
                                  icon={RotateCcw}
                                  variant="ghost"
                                  onClick={() => resetSpecificationTemplate(item.id)}
                                  disabled={!item.specTemplateId || !activeTemplate || isExecutionLocked}
                                  style={{ padding: "11px 14px" }}
                                >
                                  Reset to Template
                                </Button>

                                <Button
                                  icon={Trash2}
                                  variant="ghost"
                                  onClick={() => clearSpecificationTemplate(item.id)}
                                  disabled={(!item.specTemplateId && !item.specTemplateName) || isExecutionLocked}
                                  style={{ padding: "11px 14px" }}
                                >
                                  Clear Template
                                </Button>
                              </div>

                              {isExecutionLocked ? (
                                <div
                                  style={{
                                    padding: 12,
                                    borderRadius: 14,
                                    background: isDark ? "rgba(37,99,235,0.12)" : "#eff6ff",
                                    border: "1px solid rgba(37,99,235,0.18)",
                                    color: isDark ? "#bfdbfe" : "#1d4ed8",
                                    fontSize: 12,
                                    lineHeight: 1.6,
                                  }}
                                >
                                  This item is now managed in Execution Details. Specifications remain visible here as a read-only snapshot.
                                </div>
                              ) : null}

                              {item.specTemplateId && !activeTemplate ? (
                                <div
                                  style={{
                                    padding: 12,
                                    borderRadius: 14,
                                    background: isDark ? "rgba(245,158,11,0.12)" : "#fffbeb",
                                    border: "1px solid rgba(245,158,11,0.22)",
                                    color: isDark ? "#fde68a" : "#92400e",
                                    fontSize: 12,
                                    lineHeight: 1.6,
                                  }}
                                >
                                  This item is still linked to the saved template{" "}
                                  <strong>{item.specTemplateName || "template"}</strong>, but that
                                  template is no longer in the current workspace list. Existing specs
                                  remain editable.
                                </div>
                              ) : null}

                              {itemSpecifications.length === 0 ? (
                                <div
                                  style={{
                                    padding: 16,
                                    borderRadius: 16,
                                    background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
                                    border: isDark
                                      ? "1px solid rgba(148,163,184,0.12)"
                                      : "1px solid rgba(226,232,240,0.85)",
                                    fontSize: 13,
                                    color: isDark ? "#94a3b8" : "#64748b",
                                  }}
                                >
                                  Add structured specifications for this item or apply a template to
                                  auto-fill them.
                                </div>
                              ) : (
                                <div style={{ display: "grid", gap: 10 }}>
                                  {itemSpecifications.map((specification, specIndex) => (
                                    <div
                                      key={
                                        specification.id ||
                                        `${item.id}-spec-field-${specIndex}`
                                      }
                                      style={{
                                        padding: 12,
                                        borderRadius: 16,
                                        background: isDark ? "#020617" : "#ffffff",
                                        border: isDark
                                          ? "1px solid rgba(148,163,184,0.14)"
                                          : "1px solid #e2e8f0",
                                        display: "grid",
                                        gridTemplateColumns: isCompact
                                          ? "1fr"
                                          : "minmax(180px, 0.92fr) minmax(220px, 1.08fr) auto",
                                        gap: 10,
                                        alignItems: "center",
                                      }}
                                    >
                                      <input
                                        style={fieldStyle(isDark)}
                                        value={specification.key ?? ""}
                                        readOnly={isExecutionLocked}
                                        onChange={(e) =>
                                          updateSpecificationField(item.id, specIndex, {
                                            key: e.target.value,
                                          })
                                        }
                                        placeholder="Field name"
                                      />
                                      <input
                                        style={fieldStyle(isDark)}
                                        value={specification.value ?? ""}
                                        readOnly={isExecutionLocked}
                                        onChange={(e) =>
                                          updateSpecificationField(item.id, specIndex, {
                                            value: e.target.value,
                                          })
                                        }
                                        placeholder="Value"
                                      />
                                      <Button
                                        icon={Trash2}
                                        variant="danger"
                                        onClick={() => removeSpecificationField(item.id, specIndex)}
                                        disabled={isExecutionLocked}
                                        style={{ padding: "11px 14px" }}
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: isCompact
                              ? "1fr"
                              : "minmax(0, 1fr) auto auto",
                            gap: 12,
                            alignItems: "end",
                          }}
                        >
                          <div>
                            <label style={fieldLabelStyle(isDark)}>Product URL</label>
                            <div style={{ position: "relative" }}>
                              <input
                                style={{ ...fieldStyle(isDark), paddingLeft: 42 }}
                                value={item.productUrl || ""}
                                onChange={(e) => updateItem(item.id, "productUrl", e.target.value)}
                                placeholder="https://example.com/product"
                              />
                              <Link2
                                size={16}
                                style={{
                                  position: "absolute",
                                  left: 14,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  color: isDark ? "#94a3b8" : "#64748b",
                                }}
                              />
                            </div>
                          </div>

                          <label
                            htmlFor={`general-item-image-${item.id}`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 8,
                              padding: "11px 14px",
                              borderRadius: 14,
                              cursor: hasUploadInProgress ? "not-allowed" : "pointer",
                              background: isDark ? "#1e293b" : "#eff6ff",
                              color: "#2563eb",
                              fontWeight: 700,
                              fontSize: 14,
                              whiteSpace: "nowrap",
                              opacity: hasUploadInProgress ? 0.64 : 1,
                              pointerEvents: hasUploadInProgress || !canChangeImages ? "none" : "auto",
                            }}
                          >
                            <ImagePlus size={16} />
                            {uploadingItemId === item.id
                              ? "Uploading..."
                              : itemImages.length > 0
                              ? "Add More Images"
                              : "Add Images"}
                          </label>

                          <Button
                            icon={NotebookText}
                            variant="ghost"
                            onClick={() => toggleDescription(item.id)}
                            style={{
                              padding: "11px 14px",
                              background: isDark ? "#111827" : "#f1f5f9",
                            }}
                          >
                            {item.showDescription ? "Hide Description" : "Add Description"}
                          </Button>
                        </div>

                        <input
                          id={`general-item-image-${item.id}`}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          multiple
                          disabled={hasUploadInProgress || !canChangeImages}
                          style={{ display: "none" }}
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            if (files.length > 0) {
                              await onUploadItemImage(item.id, files);
                            }
                            e.target.value = "";
                          }}
                        />

                        {itemImages.length > 0 ? (
                          <div
                            style={{
                              display: "grid",
                              gap: 12,
                              fontSize: 12,
                              color: isDark ? "#94a3b8" : "#64748b",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 10,
                              }}
                            >
                              {itemImages.map((image, imageIndex) => {
                                const displayUrl = getImageDisplayUrl(image);
                                const fullImageUrl = getImageFullUrl(image) || displayUrl;
                                const imageRepairKey = `${item?.id || ""}:${image?.id || imageIndex}`;
                                const repairState = imageRepairState?.[imageRepairKey] || null;
                                const imageFailed =
                                  displayUrl &&
                                  imageLoadFailures[getImageFailureKey(item, image, displayUrl)];
                                const usableDisplayUrl = imageFailed ? "" : displayUrl;
                                return (
                                <div
                                  key={image.id}
                                  style={{
                                    width: 92,
                                    borderRadius: 16,
                                    overflow: "hidden",
                                    border: isDark
                                      ? "1px solid rgba(148,163,184,0.18)"
                                      : "1px solid #dbe4f0",
                                    background: isDark ? "#0f172a" : "#ffffff",
                                  }}
                                >
                                  {usableDisplayUrl ? (
                                    <a
                                      href={fullImageUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={() => handleImageOpenClick(item, image, fullImageUrl)}
                                      style={{
                                        display: "block",
                                        width: "100%",
                                        height: 72,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <img
                                        src={usableDisplayUrl}
                                        alt={image.filename || `Item image ${imageIndex + 1}`}
                                        onError={() =>
                                          markImageLoadFailed(item, image, usableDisplayUrl)
                                        }
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    </a>
                                  ) : (
                                    <div
                                      title={getImageUnavailableBody(image, repairState)}
                                      style={{
                                        width: "100%",
                                        height: 72,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        textAlign: "center",
                                        color: isDark ? "#cbd5e1" : "#475569",
                                        background: isDark ? "#111827" : "#f1f5f9",
                                        fontSize: 10,
                                        lineHeight: 1.3,
                                        padding: 6,
                                        boxSizing: "border-box",
                                      }}
                                    >
                                      <span style={{ fontWeight: 900 }}>Image unavailable</span>
                                      <span>{getImageUnavailableBody(image, repairState)}</span>
                                      {renderUnavailableImageActions(item, image, imageIndex, true)}
                                    </div>
                                  )}
                                  <div style={{ padding: 8, display: "grid", gap: 6 }}>
                                    <div
                                      style={{
                                        fontSize: 11,
                                        lineHeight: 1.35,
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {image.filename || `Image ${imageIndex + 1}`}
                                    </div>
                                    {image.expiresAt ? (
                                      <div style={{ fontSize: 10, opacity: 0.85 }}>
                                        Until {new Date(image.expiresAt).toLocaleDateString()}
                                      </div>
                                    ) : null}
                                    <div
                                      style={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 6,
                                      }}
                                    >
                                      <button
                                        type="button"
                                        onClick={() => moveItemImage(item.id, image.id, -1)}
                                        disabled={imageIndex === 0}
                                        style={{
                                          border: "none",
                                          background: isDark ? "#111827" : "#f1f5f9",
                                          borderRadius: 8,
                                          padding: "4px 6px",
                                          cursor: imageIndex === 0 ? "not-allowed" : "pointer",
                                          opacity: imageIndex === 0 ? 0.4 : 1,
                                          fontSize: 11,
                                        }}
                                      >
                                        ←
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => moveItemImage(item.id, image.id, 1)}
                                        disabled={imageIndex === itemImages.length - 1}
                                        style={{
                                          border: "none",
                                          background: isDark ? "#111827" : "#f1f5f9",
                                          borderRadius: 8,
                                          padding: "4px 6px",
                                          cursor:
                                            imageIndex === itemImages.length - 1
                                              ? "not-allowed"
                                              : "pointer",
                                          opacity:
                                            imageIndex === itemImages.length - 1 ? 0.4 : 1,
                                          fontSize: 11,
                                        }}
                                      >
                                        →
                                      </button>
                                      <button
                                        type="button"
                                        disabled={!canChangeImages}
                                        onClick={() => onRemoveItemImage(item.id,
                                          image.fullPath || image.storagePath || image.downloadURL || image.url || image.id)}
                                        style={{
                                          border: "none",
                                          background: "#fee2e2",
                                          color: "#b91c1c",
                                          borderRadius: 8,
                                          padding: "4px 8px",
                                          cursor: "pointer",
                                          fontSize: 11,
                                          fontWeight: 700,
                                        }}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                );
                              })}
                            </div>

                            {previewLink ? (
                              <a
                                href={previewLink}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  color: "#2563eb",
                                  textDecoration: "none",
                                  fontWeight: 700,
                                }}
                              >
                                Open Product / Preview Link
                              </a>
                            ) : null}
                          </div>
                        ) : null}

                        {item.showDescription ? (
                          <div>
                            <label style={fieldLabelStyle(isDark)}>Item Description</label>
                            <textarea
                              style={fieldStyle(isDark, true)}
                              value={item.description || ""}
                              onChange={(e) => updateItem(item.id, "description", e.target.value)}
                              placeholder="Add specifications, finishes, inclusions, lead time, or scope details."
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 20, width: "100%" }}>
          <div style={cardStyle(isDark)}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <div style={iconBadgeStyle(isDark)}>
                <ReceiptText size={18} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: isDark ? "#f8fafc" : "#0f172a",
                  }}
                >
                  Totals
                </div>
                <div style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b" }}>
                  Clear, client-ready pricing
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gap: 16 }}>
              <StatRow
                label="Subtotal"
                value={`${currencySymbol} ${Number(
                  totals.subtotalExcludingGst ?? totals.subtotal ?? 0
                ).toFixed(2)}`}
                isDark={isDark}
              />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "minmax(0, 0.9fr) minmax(0, 1.1fr)",
                  gap: 12,
                }}
              >
                <div>
                  <label style={fieldLabelStyle(isDark)}>Quotation Discount Type</label>
                  <select
                    style={fieldStyle(isDark)}
                    value={quote.discountType === "percent" ? "percent" : "flat"}
                    onChange={(e) => updateQuote("discountType", e.target.value)}
                  >
                    <option value="flat">Flat Discount</option>
                    <option value="percent">Percentage</option>
                  </select>
                </div>
                <div>
                  <label style={fieldLabelStyle(isDark)}>
                    {quote.discountType === "percent"
                      ? "Post-GST Discount (%)"
                      : `Post-GST Discount (${currencySymbol})`}
                  </label>
                  <input
                    type="number"
                    style={fieldStyle(isDark)}
                    value={quote.discountValue || ""}
                    onChange={(e) => updateQuote("discountValue", e.target.value)}
                    placeholder="0"
                  />
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      lineHeight: 1.4,
                      color: isDark ? "#94a3b8" : "#64748b",
                    }}
                  >
                    Applied after GST to adjust the final payable amount.
                  </div>
                </div>
              </div>

              <StatRow
                label="Item GST"
                value={`${currencySymbol} ${Number(totals.itemsGstTotal || 0).toFixed(2)}`}
                isDark={isDark}
              />

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "minmax(0, 1fr) 140px 120px 140px",
                  gap: 12,
                }}
              >
                <div>
                  <label style={fieldLabelStyle(isDark)}>Charge Label</label>
                  <input
                    style={fieldStyle(isDark)}
                    value={quote.additionalChargesLabel || ""}
                    onChange={(e) => updateQuote("additionalChargesLabel", e.target.value)}
                    placeholder="Additional Charges"
                  />
                </div>
                <div>
                  <label style={fieldLabelStyle(isDark)}>Amount</label>
                  <input
                    type="number"
                    style={fieldStyle(isDark)}
                    value={quote.additionalChargesValue || ""}
                    onChange={(e) => updateQuote("additionalChargesValue", e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label style={fieldLabelStyle(isDark)}>GST %</label>
                  <select
                    style={fieldStyle(isDark)}
                    value={Number(quote.additionalChargeGstPercent || 0)}
                    onChange={(e) =>
                      updateQuote("additionalChargeGstPercent", Number(e.target.value))
                    }
                  >
                    <option value={0}>0%</option>
                    <option value={5}>5%</option>
                    <option value={12}>12%</option>
                    <option value={18}>18%</option>
                  </select>
                </div>
                <div>
                  <label style={fieldLabelStyle(isDark)}>GST Mode</label>
                  <select
                    style={fieldStyle(isDark)}
                    value={quote.additionalChargeGstMode || "exclusive"}
                    onChange={(e) =>
                      updateQuote("additionalChargeGstMode", e.target.value)
                    }
                  >
                    <option value="inclusive">Inclusive</option>
                    <option value="exclusive">Exclusive</option>
                  </select>
                </div>
              </div>

              <StatRow
                label={quote.additionalChargesLabel || "Additional Charges"}
                value={`${currencySymbol} ${totals.additionalCharges.toFixed(2)}`}
                isDark={isDark}
              />

              <StatRow
                label={`${quote.additionalChargesLabel || "Additional Charges"} GST`}
                value={`${currencySymbol} ${Number(
                  totals.additionalChargesGst || 0
                ).toFixed(2)}`}
                isDark={isDark}
              />

              <StatRow
                label="Total GST"
                value={`${currencySymbol} ${Number(
                  totals.totalGstAmount ?? totals.gstTotal ?? 0
                ).toFixed(2)}`}
                isDark={isDark}
              />

              <StatRow
                label="Total After GST"
                value={`${currencySymbol} ${Number(
                  totals.totalAfterGst ?? totals.subtotal ?? 0
                ).toFixed(2)}`}
                isDark={isDark}
              />

              <StatRow
                label="Post-GST Discount"
                value={`- ${currencySymbol} ${Number(totals.discountAmount || 0).toFixed(2)}`}
                isDark={isDark}
              />

              <div
                style={{
                  height: 1,
                  background: isDark
                    ? "rgba(148,163,184,0.16)"
                    : "rgba(148,163,184,0.25)",
                }}
              />

              <StatRow
                label="Grand Total"
                value={`${currencySymbol} ${totals.grandTotal.toFixed(2)}`}
                highlight
                isDark={isDark}
              />

              <div
                style={{
                  padding: 16,
                  borderRadius: 16,
                  background: isDark ? "#020617" : "#f8fafc",
                  border: isDark
                    ? "1px solid rgba(148,163,184,0.16)"
                    : "1px solid #e2e8f0",
                }}
              >
                <div style={fieldLabelStyle(isDark)}>Total In Words</div>
                <div
                  style={{
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: isDark ? "#e2e8f0" : "#334155",
                  }}
                >
                  {totals.totalInWords}
                </div>
              </div>
            </div>
          </div>

          <div style={cardStyle(isDark)}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <div style={iconBadgeStyle(isDark)}>
                <NotebookText size={18} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: isDark ? "#f8fafc" : "#0f172a",
                  }}
                >
                  Notes
                </div>
                <div style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b" }}>
                  Internal highlights or customer-facing notes
                </div>
              </div>
            </div>
            <textarea
              style={fieldStyle(isDark, true)}
              value={quote.notes || ""}
              onChange={(e) => updateQuote("notes", e.target.value)}
              placeholder="Add delivery notes, measurements confirmation, or installation remarks."
            />
          </div>

          <div style={cardStyle(isDark)}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
              <div style={iconBadgeStyle(isDark)}>
                <ShieldCheck size={18} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: isDark ? "#f8fafc" : "#0f172a",
                  }}
                >
                  Terms & Conditions
                </div>
                <div style={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b" }}>
                  Add payment, validity, delivery, or warranty clauses
                </div>
              </div>
            </div>
            <textarea
              style={fieldStyle(isDark, true)}
              value={quote.terms || ""}
              onChange={(e) => updateQuote("terms", e.target.value)}
              placeholder="Outline payment terms, scope, approvals, and delivery conditions."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
